import { useEffect, useId, useState, type ReactNode } from 'react';
import { Button } from './Button';
import { Input, Select } from './Input';

/**
 * The states of a number shown before it is used (the shape `watchUpfrontNumber` in
 * @ecobox/firebase gives; written out here so this package does not depend on that one).
 */
export type UpfrontNumberView =
  | { status: 'loading' }
  | { status: 'ready'; number: string; changedFrom?: string | undefined }
  | { status: 'offline_empty'; message: string }
  | { status: 'error'; message: string };

/** A numbering series to pick (03 §3.5): its id, a name, and what its numbers look like. */
export interface UpfrontSeriesOption {
  id: string;
  label: string;
  example?: string;
}

/** A tracking number typed by hand instead of the generated one (D-27). */
export interface UpfrontCustom {
  value: string;
  /** Why it cannot be one, in words, or null. */
  problem: string | null;
  onChange: (value: string) => void;
  /** Back to the generated number. */
  onBack: () => void;
}

export interface UpfrontNumberProps {
  state: UpfrontNumberView;
  /** What the number is. Default "Tracking number". */
  label?: string;
  /** What to do with it, shown under the number. */
  hint?: ReactNode;
  /** The words for "no numbers on this device and offline". */
  offlineText?: ReactNode;
  onRetry?: () => void;
  /** The person has seen a changed number (`changedFrom`) and goes on with the new one. */
  onAccept?: () => void;
  /** Where the number goes, for the change notice. Default "on the airway bill". */
  writeOn?: string;
  /** The numbering series to choose from; shown as a picker when there is more than one. */
  series?: {
    options: readonly UpfrontSeriesOption[];
    value: string;
    onChange: (id: string) => void;
    /** Why this series was chosen, e.g. "the customer's series". */
    why?: string | undefined;
  };
  /** Type a number by hand instead (needs the permission): shows an Edit button. */
  onEdit?: () => void;
  /** Set while a number typed by hand is used instead of the generated one. */
  custom?: UpfrontCustom | undefined;
  'data-tour'?: string;
}

/**
 * The tracking number of the shipment being taken, shown from the first step of the form in big
 * mono digits with a Copy button, so staff can write it on the airway bill or the box and tell the
 * customer before anything is saved (05 New shipment, 05 §5.2 pickup stop). The numbering series
 * can be picked, and with the permission the number can be typed by hand instead.
 */
export function UpfrontNumber({
  state,
  label = 'Tracking number',
  hint,
  offlineText,
  onRetry,
  onAccept,
  writeOn = 'on the airway bill',
  series,
  onEdit,
  custom,
  'data-tour': tour,
}: UpfrontNumberProps) {
  const ids = useId();
  // What the last Copy did, and for which number: a new number shows a fresh Copy button.
  const [copyResult, setCopyResult] = useState<{ number: string; ok: boolean } | null>(null);
  const generated = state.status === 'ready' ? state.number : null;
  const number = custom ? (custom.problem === null ? custom.value : null) : generated;
  const changedFrom = !custom && state.status === 'ready' ? state.changedFrom : undefined;
  const copied = copyResult && copyResult.number === number ? (copyResult.ok ? 'yes' : 'no') : null;
  useEffect(() => {
    if (!copyResult) return;
    const timer = setTimeout(() => setCopyResult(null), 2500);
    return () => clearTimeout(timer);
  }, [copyResult]);

  const copy = async (): Promise<void> => {
    if (!number) return;
    try {
      await navigator.clipboard.writeText(number);
      setCopyResult({ number, ok: true });
    } catch {
      setCopyResult({ number, ok: false });
    }
  };

  const picker =
    series && series.options.length > 1 && !custom ? (
      <div className="eb-upfront__series">
        <label htmlFor={`${ids}-series`}>Series</label>
        <Select
          id={`${ids}-series`}
          value={series.value}
          onChange={(e) => series.onChange(e.target.value)}
        >
          {series.options.map((o) => (
            <option key={o.id} value={o.id}>
              {o.example ? `${o.label} · ${o.example}` : o.label}
            </option>
          ))}
        </Select>
      </div>
    ) : null;

  return (
    <section
      className="eb-upfront"
      data-status={custom ? 'custom' : state.status}
      data-changed={changedFrom ? 'true' : undefined}
      aria-label={label}
      data-tour={tour}
    >
      <div className="eb-upfront__main">
        <div className="eb-upfront__label">
          {custom ? `${label}, typed by hand` : label}
          {changedFrom ? <span className="eb-upfront__changed">Changed</span> : null}
        </div>
        {picker}
        {custom ? (
          <>
            <Input
              id={`${ids}-custom`}
              className="eb-upfront__input"
              aria-label={`${label} typed by hand`}
              aria-describedby={`${ids}-custom-why`}
              invalid={custom.problem !== null}
              value={custom.value}
              maxLength={24}
              autoComplete="off"
              spellCheck={false}
              autoFocus
              onChange={(e) => custom.onChange(e.target.value)}
            />
            <div
              id={`${ids}-custom-why`}
              className={custom.problem ? 'eb-upfront__problem' : 'eb-upfront__hint'}
              role={custom.problem ? 'alert' : undefined}
            >
              {custom.problem ??
                'A number already used by another shipment is refused when the shipment reaches the server.'}
            </div>
          </>
        ) : state.status === 'ready' ? (
          <div className="eb-bignumber eb-upfront__number" aria-label={`${label} of this shipment`}>
            {state.number}
          </div>
        ) : state.status === 'loading' ? (
          <div className="eb-upfront__wait" role="status">
            <span className="eb-spinner" aria-hidden="true" /> Getting a {label.toLowerCase()}…
          </div>
        ) : state.status === 'offline_empty' ? (
          <div className="eb-upfront__problem" role="status">
            <strong>{state.message}.</strong>{' '}
            {offlineText ??
              'You can fill in the form now. The number appears here as soon as this device is back online, and the shipment can be saved then.'}
          </div>
        ) : (
          <div className="eb-upfront__problem" role="alert">
            {state.message}
          </div>
        )}
        {!custom && series?.why && series.options.length > 1 ? (
          <div className="eb-upfront__hint">{series.why}</div>
        ) : null}
        {!custom && state.status === 'ready' && hint && !changedFrom ? (
          <div className="eb-upfront__hint">{hint}</div>
        ) : null}
        {changedFrom ? (
          <div className="eb-upfront__problem" role="alert">
            <strong>The {label.toLowerCase()} changed.</strong> {changedFrom} is no longer free:
            this device has used it already (in another tab or window, or for a save that did not go
            through). This shipment will be {generated}. Write the new number {writeOn} and tell the
            customer, then press Use {generated}.
          </div>
        ) : null}
      </div>
      <div className="eb-upfront__actions">
        {number ? (
          <Button variant="secondary" onClick={() => void copy()} aria-label={`Copy ${number}`}>
            {copied === 'yes' ? 'Copied' : 'Copy'}
          </Button>
        ) : null}
        {changedFrom && onAccept ? (
          <Button variant="primary" onClick={onAccept}>
            Use {generated}
          </Button>
        ) : null}
        {!custom && state.status === 'error' && onRetry ? (
          <Button variant="secondary" onClick={onRetry}>
            Retry
          </Button>
        ) : null}
        {!custom && onEdit ? (
          <Button
            variant="ghost"
            onClick={onEdit}
            aria-label={`Type the ${label.toLowerCase()} by hand`}
          >
            Edit
          </Button>
        ) : null}
        {custom ? (
          <Button variant="secondary" onClick={custom.onBack}>
            Back to generated number
          </Button>
        ) : null}
        {copied === 'no' ? (
          <span className="eb-upfront__hint" role="status">
            Could not copy. Select the number instead.
          </span>
        ) : null}
      </div>
    </section>
  );
}
